import axios from 'axios'
import { Message, MessageBox } from 'element-ui'

// 业务码约定：200 成功，401 未授权，其它为错误
const SUCCESS_CODE = 200
const TOKEN_KEY = 'cityu_press_token'

export const getToken = () => localStorage.getItem(TOKEN_KEY)
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token)
export const removeToken = () => localStorage.removeItem(TOKEN_KEY)

// 防止重复弹出登录过期提示
let isReloginShown = false

// 防止频繁调用接口：记录 pending 中的请求，重复请求将被自动取消
// key: 由 method + url + params + data 生成；value: axios CancelToken 的 cancel 函数
const pendingRequests = new Map()

const generateRequestKey = (config) => {
  const { method = 'get', url = '', params, data } = config
  return [
    method.toLowerCase(),
    url,
    JSON.stringify(params || {}),
    typeof data === 'string' ? data : JSON.stringify(data || {})
  ].join('&')
}

const addPendingRequest = (config) => {
  const key = generateRequestKey(config)
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken((cancel) => {
      if (!pendingRequests.has(key)) {
        pendingRequests.set(key, cancel)
      }
    })
}

const removePendingRequest = (config) => {
  if (!config) return
  const key = generateRequestKey(config)
  if (pendingRequests.has(key)) {
    const cancel = pendingRequests.get(key)
    cancel(key)
    pendingRequests.delete(key)
  }
}

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 允许调用方通过 { skipDedupe: true } 关闭去重（例如文件上传、轮询场景）
    if (!config.skipDedupe) {
      removePendingRequest(config)
      addPendingRequest(config)
    }
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截器
service.interceptors.response.use(
  (res) => {
    // 请求完成后从 pending 列表中移除
    removePendingRequest(res.config)

    // 二进制流直接返回
    const responseType = res.request?.responseType
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      return res.data
    }

    const { code = SUCCESS_CODE, msg = '请求失败' } = res.data || {}

    if (code === SUCCESS_CODE) {
      return res.data
    }

    if (code === 401) {
      if (!isReloginShown) {
        isReloginShown = true
        MessageBox.confirm('登录状态已过期，请重新登录', '系统提示', {
          confirmButtonText: '重新登录',
          cancelButtonText: '取消',
          type: 'warning'
        })
          .then(() => {
            removeToken()
            location.reload()
          })
          .finally(() => {
            isReloginShown = false
          })
      }
      return Promise.reject(new Error('会话已过期'))
    }

    Message({ message: msg, type: 'error', duration: 3000 })
    return Promise.reject(new Error(msg))
  },
  (error) => {
    // 被去重逻辑主动取消的请求，静默丢弃，不提示错误
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }
    removePendingRequest(error.config)

    let message = error.message || '请求失败'
    if (message === 'Network Error') {
      message = '后端接口连接异常'
    } else if (message.includes('timeout')) {
      message = '系统接口请求超时'
    } else if (message.includes('Request failed with status code')) {
      message = '系统接口 ' + message.slice(-3) + ' 异常'
    }
    Message({ message, type: 'error', duration: 3000 })
    return Promise.reject(error)
  }
)

// 便捷方法
export const get = (url, params, config = {}) =>
  service({ url, method: 'get', params, ...config })

export const post = (url, data, config = {}) =>
  service({ url, method: 'post', data, ...config })

export const put = (url, data, config = {}) =>
  service({ url, method: 'put', data, ...config })

export const del = (url, params, config = {}) =>
  service({ url, method: 'delete', params, ...config })

export default service
